// Client-side mirror of internal/fdm/fdm.go, used for prediction of the
// local plane between server snapshots. It does not need bit-parity with the
// server (soft reconciliation absorbs drift) but the math is kept line-for-
// line equivalent so predicted flight feels identical to authoritative
// flight. Frames: world X east / Y up / Z south; body +X fwd / +Y up / +Z right.

export const GRAVITY = 9.81;
const SEA_LEVEL_RHO = 1.225;
const RHO_SCALE_H = 8500.0;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// --- tiny vec/quat kit (plain objects, mirrors internal/mathx) ---
export const v3 = (x = 0, y = 0, z = 0) => ({ x, y, z });
export const vAdd = (a, b) => v3(a.x + b.x, a.y + b.y, a.z + b.z);
export const vSub = (a, b) => v3(a.x - b.x, a.y - b.y, a.z - b.z);
export const vScale = (a, s) => v3(a.x * s, a.y * s, a.z * s);
export const vDot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
export const vLen = (a) => Math.sqrt(vDot(a, a));
export const vCross = (a, b) =>
  v3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
export function vNorm(a) {
  const l = vLen(a);
  return l < 1e-12 ? v3() : vScale(a, 1 / l);
}

export const q4 = (x = 0, y = 0, z = 0, w = 1) => ({ x, y, z, w });

export function qMul(q, r) {
  return q4(
    q.w * r.x + q.x * r.w + q.y * r.z - q.z * r.y,
    q.w * r.y - q.x * r.z + q.y * r.w + q.z * r.x,
    q.w * r.z + q.x * r.y - q.y * r.x + q.z * r.w,
    q.w * r.w - q.x * r.x - q.y * r.y - q.z * r.z,
  );
}

export const qConj = (q) => q4(-q.x, -q.y, -q.z, q.w);

export function qNorm(q) {
  const l = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);
  return l < 1e-12 ? q4() : q4(q.x / l, q.y / l, q.z / l, q.w / l);
}

export function qRotate(q, v) {
  const qv = v3(q.x, q.y, q.z);
  const t = vScale(vCross(qv, v), 2);
  return vAdd(vAdd(v, vScale(t, q.w)), vCross(qv, t));
}

export function qIntegrate(q, omega, dt) {
  const dq = qMul(q, q4(omega.x, omega.y, omega.z, 0));
  return qNorm(
    q4(
      q.x + 0.5 * dt * dq.x,
      q.y + 0.5 * dt * dq.y,
      q.z + 0.5 * dt * dq.z,
      q.w + 0.5 * dt * dq.w,
    ),
  );
}

export function airDensity(alt) {
  if (alt < 0) alt = 0;
  return SEA_LEVEL_RHO * Math.exp(-alt / RHO_SCALE_H);
}

export function newState() {
  return { pos: v3(), vel: v3(), att: q4(), omega: v3() };
}

function liftCoefficient(cfg, alpha) {
  let cl = clamp(cfg.cl0 + cfg.clAlpha * alpha, -cfg.clMax, cfg.clMax);
  const a = Math.abs(alpha);
  if (a > cfg.alphaStallRad) {
    cl *= clamp(1 - (a - cfg.alphaStallRad) / (1.3 * cfg.alphaStallRad), 0.15, 1);
  }
  return cl;
}

// Identity damage modifiers: a pristine airframe. Mirrors fdm.NoDamage().
export function noDamage() {
  return { thrustScale: 1, rollAuthMul: 1, rollBias: 0, controlScale: 1,
    pitchAuthMul: 1, yawAuthMul: 1 };
}

/**
 * Map subsystem health {engine,wingL,wingR,fuel,pilot,tail} to FDM modifiers.
 * Mirrors game.DeriveDamageMods — keep them in lockstep. `combat` is the
 * config.combat block.
 */
export function deriveDamageMods(d, combat) {
  let enginePower = clamp(d.engine, 0, 1);
  if (d.fuel <= 0) enginePower = 0; // a dry tank starves the engine
  const wingL = clamp(d.wingL, 0, 1);
  const wingR = clamp(d.wingR, 0, 1);
  const tail = clamp(d.tail ?? 1, 0, 1);
  return {
    thrustScale: combat.thrustFloor + (1 - combat.thrustFloor) * enginePower,
    rollAuthMul: 0.5 * wingL + 0.5 * wingR,
    rollBias: combat.wingBiasK * (wingL - wingR), // toward the more-damaged wing
    controlScale: combat.controlFloor + (1 - combat.controlFloor) * clamp(d.pilot, 0, 1),
    // A shredded empennage mushes elevators and rudder together — no HUD
    // bar for this; the stick going soft IS the telling.
    pitchAuthMul: combat.controlFloor + (1 - combat.controlFloor) * tail,
    yawAuthMul: combat.controlFloor + (1 - combat.controlFloor) * tail,
  };
}

/**
 * Advance state by dt seconds. cfg is the aircraft block of /config.json;
 * ctl is {pitch, roll, yaw, throttle} normalized. dmg is the FDM damage
 * modifier set (see deriveDamageMods); omit it for a pristine airframe.
 */
export function step(cfg, s, ctl, dt, dmg = noDamage()) {
  // Pilot greyout mushes the flight controls (not the throttle lever).
  const pitch = clamp(ctl.pitch, -1, 1) * dmg.controlScale;
  const rollIn = clamp(ctl.roll, -1, 1) * dmg.controlScale;
  const yawIn = clamp(ctl.yaw, -1, 1) * dmg.controlScale;
  const throttle = clamp(ctl.throttle, 0, 1);

  const speed = vLen(s.vel);
  const rho = airDensity(s.pos.y);

  const fwd = qRotate(s.att, v3(1, 0, 0));
  const up = qRotate(s.att, v3(0, 1, 0));
  const right = qRotate(s.att, v3(0, 0, 1));

  let force = v3(0, -GRAVITY * cfg.massKg, 0);
  const moment = v3();

  let thrust = cfg.maxThrustN * throttle * dmg.thrustScale;
  if (cfg.thrustFalloffV > 0) {
    thrust *= clamp(1 - speed / (cfg.thrustFalloffV * 2), 0.3, 1);
  }
  force = vAdd(force, vScale(fwd, thrust));

  let alpha = 0;
  if (speed > 1.0) {
    const vhat = vScale(s.vel, 1 / speed);
    const u = vDot(s.vel, fwd);
    const w = vDot(s.vel, up);
    const v = vDot(s.vel, right);

    alpha = Math.atan2(-w, Math.max(u, 1.0));
    const beta = Math.atan2(v, Math.max(u, 1.0));

    const qbar = 0.5 * rho * speed * speed;
    const qS = qbar * cfg.wingAreaM2;

    const cl = liftCoefficient(cfg, alpha);
    const cd = cfg.cd0 + cfg.inducedDragK * cl * cl + 0.6 * beta * beta;

    const liftDir = vNorm(vCross(right, vhat));
    force = vAdd(force, vScale(liftDir, qS * cl));
    force = vAdd(force, vScale(vhat, -qS * cd));
    force = vAdd(force, vScale(right, qS * cfg.cyBeta * beta));

    const v2 = Math.max(speed, 12.0);
    const pHat = (s.omega.x * cfg.wingSpanM) / (2 * v2);
    const rHat = (s.omega.y * cfg.wingSpanM) / (2 * v2);
    const qHat = (s.omega.z * cfg.chordM) / (2 * v2);

    // A damaged wing loses aileron authority (rollAuthMul) and adds a steady
    // rolling moment toward the hurt side (rollBias); a shredded empennage
    // mushes the elevator and rudder (pitch/yawAuthMul) while the
    // weathervane stability terms keep flying.
    const cRoll =
      cfg.clRollAileron * rollIn * dmg.rollAuthMul + dmg.rollBias + cfg.clRollRate * pHat + cfg.clRollBeta * beta;
    const cYaw = cfg.cnYawRudder * yawIn * (dmg.yawAuthMul ?? 1) + cfg.cnYawRate * rHat + cfg.cnYawBeta * beta;
    const cPitch =
      cfg.cm0 + cfg.cmAlpha * alpha + cfg.cmElevator * pitch * (dmg.pitchAuthMul ?? 1) + cfg.cmPitchRate * qHat;

    moment.x = qS * cfg.wingSpanM * cRoll;
    moment.y = qS * cfg.wingSpanM * cYaw;
    moment.z = qS * cfg.chordM * cPitch;
  }

  const acc = vScale(force, 1 / cfg.massKg);
  s.vel = vAdd(s.vel, vScale(acc, dt));
  s.pos = vAdd(s.pos, vScale(s.vel, dt));

  const iw = v3(
    cfg.inertiaRoll * s.omega.x,
    cfg.inertiaYaw * s.omega.y,
    cfg.inertiaPitch * s.omega.z,
  );
  const gyro = vCross(s.omega, iw);
  s.omega.x += ((moment.x - gyro.x) / cfg.inertiaRoll) * dt;
  s.omega.y += ((moment.y - gyro.y) / cfg.inertiaYaw) * dt;
  s.omega.z += ((moment.z - gyro.z) / cfg.inertiaPitch) * dt;

  s.omega.x = clamp(s.omega.x, -6, 6);
  s.omega.y = clamp(s.omega.y, -3, 3);
  s.omega.z = clamp(s.omega.z, -4, 4);

  s.att = qIntegrate(s.att, s.omega, dt);

  // Diagnostic outputs for the HUD only (the Go integrator omits these and
  // nothing here reads them back): the angle of attack used above, and the
  // accelerometer load factor along the body-up axis in g — the gravity
  // term is removed so 1 g is level flight, climbing turns read high, and a
  // pushover reads negative, exactly like a real G-meter.
  s.alpha = alpha;
  s.nz =
    speed > 1.0
      ? (force.x * up.x + (force.y + GRAVITY * cfg.massKg) * up.y + force.z * up.z) /
        (cfg.massKg * GRAVITY)
      : 1;
}

// --- the jet carve capture (mouse aim, CLASS_JET only) -------------------
// The prop pursuit below — roll the target into the lift plane, then pull —
// works because a prop's pull can actually keep up: at 100 m/s the wing turns
// the nose fast enough that the target lands in the lift plane and the roll
// command dies out at a steady carve. A jet's cannot. At 250+ m/s the 6 g
// ceiling is a turn rate of ~12 deg/s, a modest reticle offset asks for far
// more, so the target never centers, the pursuit keeps rolling "toward" it —
// through 90 degrees, where the pull stops turning and starts slicing the
// nose into the ground. The weave probe caught the whole family: a held
// quarter-deflection carves a prop at a rock-steady 78-degree bank while the
// same hand rolls a jet full circle (bank sweeping ±180 forever), and in the
// live client one firm rightward drag augured a Starfang in from 480 m in
// six seconds. Players at altitude felt the survivable version: the jet
// weaving back and forth through a held heading, never capturing it.
//
// So for jets the horizon game changes from pursuit to CAPTURE — the same
// cure the touch stick got when it wound the wings past vertical ("I only
// have rudder and elevator"): heading error commands a turn rate, the bank
// that buys it at this speed is the target (capped at the bank the G
// ceiling can actually use), and roll rate-damps onto it. The
// vertical game is untouched: aim well above (or below) the horizon and the
// carve yields back to pure pursuit, because a loop or a split-S really is
// "roll it into the lift plane and haul". Props (jetLead absent) fly the
// original pursuit bit-for-bit.
const AIM_CARVE_BANK_MAX = 1.396; // 80 deg — the 6 g carve, same ceiling as the touch deep-stick
// Heading error commands a TURN RATE (rad/s per rad of error), and the bank
// target is whatever bank buys that rate at the current speed — tan(bank) =
// omega*V/g, the level-turn law. The first cut mapped error straight to bank
// (full by ~36 deg off the nose), which is speed-blind: the same 3-degree
// bank that walks a prop's nose across the last two degrees in a second
// crawls at 0.05 deg/s at 250 m/s, and every jet in the mouse probe parked a
// standing ~1.3-degree miss it never closed — guns that almost lay on,
// forever. In rate domain the convergence clock is the same at every
// airspeed; the fast jet simply banks steeper to keep it.
const AIM_CARVE_RATE_P = 1.2; // rad/s of turn per rad of heading error
// Heading-RATE damping on the carve's bank target — the delay armor. The
// pure P target is clean at a 60 Hz zero-lag loop, but the live client's
// command travels the same laggy path autopilotLevel documents (30 Hz
// resample, prediction, slow frames), and the delay probe caught the carve
// ringing ±14 deg of bank at 150 ms of it (the live playtest saw ±50: a
// captured aim breathing left-right forever). Feeding the actual turn rate
// back gives the loop phase lead the same way every other loop here damps
// with omega; the carve stays settled past ~200 ms.
const AIM_CARVE_RATE_D = 0.45; // s of turn-rate feedback against the heading error
const AIM_CARVE_ROLL_P = 2.2; // bank error -> roll rate (STICK_ROLL_P's proven capture gain)
const AIM_CARVE_ROLL_D = 0.5; // roll-rate damping, kills the wobble at capture
const AIM_CARVE_VERT = 0.5; // |dir.y| where the carve starts yielding to pursuit (fully by 0.7)

/**
 * Mouse-aim flight assist: convert a desired world direction into control
 * inputs. Unlike the bots' pure roll-and-pull, shallow below-nose targets are
 * handled by pushing the nose down instead of rolling inverted, which is what
 * mouse-aim players expect.
 *
 * Pass `limits = { alphaStallRad, gMax }` to enable the instructor: it tapers a
 * pull command as the wing nears stall AoA or the pilot nears the G ceiling, so
 * a full thumb/mouse deflection commands "as hard as the plane can safely turn"
 * instead of an instant snap-stall. Omit it (or pass null) for the raw assist.
 * With `limits.jetLead > 0` the horizon game swaps pursuit for the jet carve
 * capture (see the block above) — jets bank onto a heading instead of rolling
 * through it.
 */
export function steerTowardAssist(state, dir, limits = null) {
  const db = qRotate(qConj(state.att), dir); // body frame: x fwd, y up, z right
  const up = qRotate(state.att, v3(0, 1, 0));
  const right = qRotate(state.att, v3(0, 0, 1));
  const bank = Math.atan2(-right.y, up.y); // +ve = banked right, as the stick instructor
  // The small-error band: inside ~1 degree of lateral error roll stands down,
  // ramping to the full pursuit by ~6 degrees. War Thunder's instructor gates
  // roll out of fine corrections exactly like this (the dirXZFactor band in
  // its datamined config) and it is why WT gun-laying feels like a turret:
  // the old always-on atan2 mapped 2 degrees of lateral error to 0.29 of
  // aileron — a see-saw of bank for every pixel of aim, the "twitchy near
  // the nose" half of the hard-to-steer reports. Fine lateral work belongs
  // to the rudder (below); honest turns still get the full roll instantly.
  const rollW = clamp((Math.abs(db.z) - 0.015) / 0.075, 0, 1);
  let roll = Math.atan2(db.z, Math.max(db.y, 0.18)) * 1.5 * rollW;
  roll -= state.omega.x * 0.35; // damp wobble
  // Rudder, the right way around, for everyone. +yaw is nose-LEFT (the stick
  // instructor documents it; the FDM agrees), and for years the prop line
  // here read `yaw = db.z * 0.5` — rudder AWAY from the target, a sign slip
  // buried under the pursuit's saturated roll. The jet carve work exposed it
  // (a standing 9-degree endgame miss) and fixed it for jets only, leaving
  // the prop feel "as its own spec" — but the field kept reporting planes
  // that wander and fight the aim, and with the roll band above handing fine
  // lateral work TO the rudder, the sign finally has to be true everywhere.
  // Coordinated into the aim, fading as the bank steepens (it lays guns near
  // wings-level, it doesn't skid a carve), plus the Dutch-roll damper the
  // stick and autopilot use.
  const levelW = Math.max(0, 1 - Math.abs(bank) / 0.79);
  let yaw = -db.z * 0.9 * levelW - state.omega.y * 0.4;
  if (limits && (limits.jetLead ?? 0) > 0) {
    const headErr = Math.atan2(db.z, db.x); // +ve = target off to the right
    // Rate-domain bank target (see AIM_CARVE_RATE_P above), with the carve's
    // own small-error gate. The prop pursuit's rollW band exists to stop
    // aileron see-saw, but the carve is a damped bank CAPTURE — its only sin
    // near the nose is wing-rock, so its band is narrower: fully open by ~2
    // degrees of lateral error (the prop band would strangle the bank across
    // the whole 1-3 degree regime and the jet parks a standing miss again),
    // closed under half a degree, where the wings stand down and the rudder
    // lays the guns — the Ironclad endgame, shared fleet-wide. Near the nose
    // both the carve and the settled-hand wings-leveller (aimdirector.js)
    // now want level: the arm-wrestle is gone by construction.
    const carveW = clamp((Math.abs(db.z) - 0.008) / 0.027, 0, 1);
    const spd = Math.max(vLen(state.vel), 60);
    // Rightward heading rate, for the damper: world-frame yaw rate, negated
    // because the house heading (atan2(-z, x)) grows to the LEFT.
    const rTurn = -qRotate(state.att, state.omega).y;
    const bankT =
      clamp(
        Math.atan(((headErr - rTurn * AIM_CARVE_RATE_D) * AIM_CARVE_RATE_P * spd) / GRAVITY),
        -AIM_CARVE_BANK_MAX,
        AIM_CARVE_BANK_MAX,
      ) * carveW;
    const capture = (bankT - bank) * AIM_CARVE_ROLL_P - state.omega.x * AIM_CARVE_ROLL_D;
    const vert = clamp((Math.abs(dir.y) - AIM_CARVE_VERT) / 0.2, 0, 1);
    // The capture's AUTHORITY fades with the same gate as its target. With
    // bankT gated but the stiffness left on, the carve inside the band is a
    // wings-leveller at P=2.2 — hotter than autopilotLevel's delay-proof 2.0
    // — and the delay probe caught the hot-rolling Starfang ringing ±14 deg
    // of bank at 150 ms of loop lag while the props sat still. Inside the
    // band a jet now carries exactly what a prop carries: rate damping, the
    // rudder, and the settled-hand leveller at its proven gains.
    const cw = carveW * (1 - vert);
    roll = capture * cw + roll * (1 - cw);
    // The carve used to carry its own rudder line at 0.7 — a leftover "its
    // own spec" from before the sign fix unified the fleet. The shared 0.9
    // line above serves the carve as-is: same gain, same wings-level fade,
    // same Dutch-roll damper, one less force competing with the props' feel.
  }
  let pitch = db.y * 2.7;
  if (db.x < 0) pitch = Math.max(pitch, 0.7); // target behind: haul it around
  // Instructor limits: taper the pull as the wing nears stall AoA or the pilot
  // nears the G ceiling, so a full thumb/mouse deflection commands "as hard as
  // the plane can safely turn" instead of an instant snap-stall. Negative
  // (push) commands are left alone — you can always unload. Applied *after* the
  // haul-around floor so a target behind you still can't drag you into a stall.
  if (limits && pitch > 0) {
    const aMax = limits.alphaStallRad * 0.85; // bite before the buffet
    // jetLead: rate anticipation for the limiter, jets only. At jet dynamic
    // pressure the G arrives faster than the nz/alpha feedback registers it,
    // so the prop-tuned limiter recoils AFTER the overshoot — pull, cut,
    // nose DIP, pull again — a rhythmic hump in every hard pull-up (the
    // porpoise report: omegaZ swung 0.37 → −0.07 → 0.15 in one second of
    // pull entry; widening the tapers instead just made the whole pull
    // limit-cycle). Leading the headroom with the pitch rate eases the
    // elevator during the rotation, before the overshoot exists — a damper,
    // not a stiffer spring. Props (jetLead absent) are bit-for-bit untouched.
    //
    // Taxed on the EXCESS rate only: a steady 6 g turn at 190 m/s carries a
    // legitimate 0.3 rad/s of pitch rate, and the first cut of this damper
    // (lead on raw omegaZ) billed it ~2 g for that — every sustained turn
    // sagged to 4 g and the field report came back "turns are too wide"
    // within two days. The rate the current G at the current speed already
    // explains (nz·g/V) is the turn, not the overshoot; only rotation
    // outrunning it — the porpoise's actual signature — pays the damper.
    const qTurn = (Math.max(state.nz ?? 1, 1) * GRAVITY) / Math.max(vLen(state.vel), 60);
    const lead = (limits.jetLead ?? 0) * Math.max(0, state.omega.z - qTurn);
    const aoaHead = clamp((aMax - (state.alpha ?? 0) - lead * 0.2) / (aMax * 0.35), 0, 1);
    const gHead = clamp((limits.gMax - (state.nz ?? 1) - lead * 5) / 1.5, 0, 1);
    pitch *= Math.min(aoaHead, gHead);
  }
  const c = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
  return { pitch: c(pitch, -0.7, 1), roll: c(roll, -1, 1), yaw: c(yaw, -0.4, 0.4) };
}

// --- touch-stick instructor (client-only, like steerTowardAssist above) ---
// A virtual joystick is not a mouse: a thumb held right means "roll into a
// turn and HOLD it", not "point the nose at a spot 30 degrees off my tail
// forever". Feeding the stick through the cursor assist did the latter, and
// the playtest numbers were grim: full right stick wound the wings past
// vertical (125 deg of bank), the coordinated pull dragged the nose 50 deg
// below the horizon, and the plane spiralled 550 m into the dirt in eight
// seconds. Testers read the wallow as "I only have rudder and elevator".
//
// This is the War-Thunder-style answer: sideways deflection commands a TURN
// the instructor captures and holds, pitch holds the flight path through the
// turn and the thumb's vertical axis adds pull/push on top, rudder stays
// coordinated. Release the stick and the same loops roll you wings-level.
//
// The first cut of this instructor mapped the stick linearly to BANK ANGLE
// and capped it at 80 deg, and the field reports were brutal: half stick was
// a 26-degree, 1.1 g stroll (3/4 stick measured 4 deg/s of turn — "slow and
// wide, impossible to dogfight"), the pitch-rate damper ate the pull so full
// up-stick couldn't even loop, and with the bank hold welded to +-80 deg the
// airplane could not be put on its back by ANY input — no split-S, no
// Immelmann exit, no rolls. The fixes, each measured by
// tools/touch_feel_probe.mjs:
//   - the stick now maps to tan(bank) — the LOAD of the level turn — so turn
//     rate is linear in deflection: half stick is a 63-degree, 2.2 g bite;
//   - the feed-forward pull is sized to saturate into the G/AoA limiter, so
//     a deep-stick turn actually rides the 6 g ceiling instead of 4 g;
//   - a hard thumb pull releases the bank hold (a loop must not be
//     arm-wrestled back upright) and out-muscles the rate damper;
//   - the flight-path hold is weighted by up.y, so it knows which way the
//     cockpit's "up" points and holds the path inverted too;
//   - slamming the stick to the rim ALONE (no pull) stops capturing and just
//     rolls — through vertical, to inverted, all the way around. Sideways
//     PLUS pull stays a max-performance turn: bank-and-yank is a turn.
const STICK_BANK_FULL = 1.396; // deep-stick bank = 80 deg — the 6 g carve
const STICK_BANK_EXPO = 1.35; // fine aim near center; the tan map restores the middle.
// Dropped from 1.5 after the second field report: a real thumb lives at
// 50-70% of the throw, and the curve was still soft exactly there — 1.35
// puts half stick at a 66-degree, 2.4 g bite while a sub-0.2 aiming touch
// stays under a 35-degree lean.
const STICK_ROLL_P = 2.2; // bank error -> roll rate; brisk capture without overshoot
const STICK_ROLL_D = 0.5; // roll-rate damping, kills the wobble at capture
// Roll-through: slam the stick to the RIM, roughly sideways, and it stops
// capturing a bank and just rolls — through vertical, to inverted, all the
// way around. That's the door to split-S, barrel rolls, and inverted flight
// that a pure bank hold welds shut. Two gates, multiplied:
//   - radial: the thumb must actually be at the stop (a 90% carve must
//     never drift into a surprise roll mid-fight);
//   - direction: within ~30 degrees of horizontal. A real thumb thrown to
//     the rim arcs 25-30 degrees off axis — the first cut gated on |sx|
//     alone at 0.92, which rejected every honest slam (the wings caught at
//     80 degrees) AND left a hover pocket where a partial blend fought the
//     bank capture to a standstill at 90 degrees. The field report for that
//     was "I can't roll past a horizontal turn", the second time it had
//     been filed. Both gates run 0 -> 1 over a short ramp so the handover
//     is decisive, not an equilibrium.
const STICK_ROLL_THRU = 0.92; // radial gate: starts here, fully open at 0.98
// Direction gate. Push side (thumb drifting away from lift): opens at 37 deg
// off horizontal — nobody carves downward, so a sloppy low slam rolls free.
// Pull side is where two honest gestures collide: a rim slam for a roll arcs
// 25-30 deg high, and a deliberate oblique carve — roll AND pull toward lift,
// the turn that isn't on the horizon's axis — sits at 30-45 deg. The first
// cut treated both sides alike, full within 30 deg, and the field report was
// a jet that "continues the roll and doesn't pull through the turn": the
// 30-deg carve read as a slam and the fighter snap-rolled forever. So the
// pull-side vertical axis is weighted up (STICK_PULL_W) before the cone
// reads the angle, and the ramp is steep: pulling toward lift, the roll-
// through door now closes between 27 and 29 deg — the 25-deg slam still
// rolls clean through, the 30-deg carve captures its bank and BITES.
const STICK_ROLL_CONE = 0.815; // direction gate floor (steep: full 0.035 higher)
const STICK_PULL_W = 1.3; // pull-side deflection counts 1.3x against the cone
const STICK_PULL_GAIN = 1.4; // thumb full-up saturates the elevator into the G/AoA
// limiter — a phone pilot must be able to LOOP. The old 0.9, minus the rate
// damper's steady-state bite, left full-up grinding uphill at 1.5 g while
// desktop mouse aim looped freely overhead.
const STICK_TURN_FF = 0.3; // feed-forward pull per unit of (1/cos(bank) - 1): the
// load factor a level turn needs grows as the bank steepens, and feeding it
// forward keeps the nose carving instead of waiting for the (deliberately
// gentle, phone-latency-proof — see autopilotLevel) feedback loop to sag first.
// Sized generous: the G/AoA limiter is the authority on "how hard", this term
// only has to make sure the ask reaches it.
const STICK_FINE_YAW = 0.15; // a breath of rudder for gun-laying at shallow bank

export function steerStickAssist(state, sx, sy, limits = null) {
  const c = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
  const up = qRotate(state.att, v3(0, 1, 0));
  const right = qRotate(state.att, v3(0, 0, 1));
  const fwd = qRotate(state.att, v3(1, 0, 0));
  const bank = Math.atan2(-right.y, up.y); // +ve = banked right, as autopilotLevel

  // The instructor only holds the horizon while it can see it. Pointed
  // near-vertical the bank angle is numerically garbage (and tactically
  // meaningless — over the top of a loop there is nothing to hold), so the
  // bank hold fades out. A hard thumb pull with the stick centered is the
  // pilot saying "I'm flying the vertical now": the hold lets go instead of
  // rolling the loop back upright; with sideways still held the hold stays,
  // because bank-and-yank is a turn, not a loop.
  const horiz = c((0.95 - Math.abs(fwd.y)) / 0.35, 0, 1);
  // pullFree engages at 0.6, not 0.5: a rim slam that drifts diagonal reads
  // ~0.4-0.5 on the vertical axis, and that incidental pull must not veto the
  // roll-through the sideways slam is asking for. A deliberate loop pulls
  // well past 0.6.
  const pullFree = c((Math.abs(sy) - 0.6) / 0.3, 0, 1);
  const auth = horiz * (1 - pullFree * (1 - Math.abs(sx)));

  // Roll: the thumb commands the TURN, not the bank angle. tan(bank) is the
  // load a level turn carries, so mapping the shaped stick onto tan keeps
  // turn rate linear in deflection — the middle of the stick's throw holds
  // the middle of the turn envelope. At the rim, with the pull released, the
  // capture blends into a pure roll command and the wings keep going.
  const mag = Math.abs(sx);
  const shaped = Math.pow(mag, STICK_BANK_EXPO);
  const bankT = Math.sign(sx) * Math.atan(shaped * Math.tan(STICK_BANK_FULL));
  // Roll-through gates (see the block comment above): radial fullness times
  // the horizontal-direction cone. touch.js scales both axes by the same
  // factor, so hypot(sx, sy) is the thumb's radial deflection and
  // |sx| / hypot is the direction cosine — the rim always reads radial 1.
  // The cone reads the PULL side through STICK_PULL_W-weighted glasses, so
  // "roll plus pull toward lift" reads as the oblique carve it is (capture
  // and bite) while the same drift on the push side stays a rolling slam.
  const rad = Math.hypot(sx, sy);
  const coneCos = mag / Math.max(Math.hypot(sx, sy > 0 ? sy * STICK_PULL_W : sy), 0.001);
  const thru =
    c((rad - STICK_ROLL_THRU) / 0.06, 0, 1) *
    c((coneCos - STICK_ROLL_CONE) / 0.035, 0, 1) *
    (1 - pullFree);
  const capture = (bankT - bank) * STICK_ROLL_P * auth - state.omega.x * STICK_ROLL_D;
  const roll = c(capture * (1 - thru) + Math.sign(sx) * thru, -1, 1);

  // Pitch: pin the flight path through the turn (same vertical-speed loop
  // and latency-proof gains as autopilotLevel), feed the turn's extra load
  // forward, and let the thumb's vertical axis pull or push on top. The hold
  // is weighted by up.y so it knows which way "up" is from the cockpit:
  // inverted it pushes to hold the path (a split-S entry isn't shoved into
  // the dirt), at knife-edge it bows out and the feed-forward carries the
  // turn.
  const vsTarget = c(-state.vel.y * 0.03, -0.1, 0.1);
  const pitchAngle = Math.asin(c(fwd.y, -1, 1));
  const ff =
    (1 / Math.max(Math.abs(Math.cos(bank)), 0.15) - 1) * STICK_TURN_FF * horiz * (1 - thru);
  // The flight-path hold AND its rate damper yield as the thumb commands
  // pitch — a deliberate loop must override the hold, not arm-wrestle it
  // (same rule as main.js's auto-trim). Hands off the vertical axis, both
  // are at full strength and the loop stays phone-latency-proof.
  const yield_ = 1 - Math.abs(sy);
  const hold = (vsTarget - pitchAngle) * 1.6 * yield_ * up.y;
  let pitch = hold - state.omega.z * 0.9 * yield_ + ff + sy * STICK_PULL_GAIN;
  // Same instructor as steerTowardAssist: full deflection commands "as hard
  // as the plane can safely turn", never a snap-stall. Push is never limited.
  // jetLead: rate-anticipated limiting for jets — the prop-tuned limiter
  // recoils at jet dynamic pressure and the thumb feels the nose hump in
  // every hard pull. Taxed on the EXCESS rate over the steady turn's nz·g/V
  // so a sustained 6 g carve isn't billed for its own pitch rate (see
  // steerTowardAssist for the mechanism and the wide-turn relapse it caused).
  if (limits && pitch > 0) {
    const aMax = limits.alphaStallRad * 0.85;
    const qTurn = (Math.max(state.nz ?? 1, 1) * GRAVITY) / Math.max(vLen(state.vel), 60);
    const lead = (limits.jetLead ?? 0) * Math.max(0, state.omega.z - qTurn);
    const aoaHead = c((aMax - (state.alpha ?? 0) - lead * 0.2) / (aMax * 0.35), 0, 1);
    const gHead = c((limits.gMax - (state.nz ?? 1) - lead * 5) / 1.5, 0, 1);
    pitch *= Math.min(aoaHead, gHead);
  }

  // Yaw: coordinated into the turn (+yaw is nose-LEFT, so stick-right feeds
  // negative rudder), fading out as the bank steepens — it exists for fine
  // gun-laying near wings-level, not for skidding the turn — plus the same
  // Dutch-roll damper the autopilot uses.
  const fine = -sx * STICK_FINE_YAW * Math.max(0, 1 - Math.abs(bank) / 0.79);
  const yaw = c(fine - state.omega.y * 0.4, -0.4, 0.4);

  return { pitch: c(pitch, -0.7, 1), roll, yaw };
}

/**
 * Straight-and-level autopilot. Holds the captured horizontal heading `dir`
 * with the wings level and the flight path pinned to the horizon.
 *
 * Unlike steerTowardAssist — which points the nose straight at a target and, on
 * a horizontal heading, weaves as it over- and under-shoots — this is a proper
 * cascaded wing-leveller:
 *   - heading error commands a small, *capped* bank angle (turn rate is
 *     proportional to bank, so a proportional heading loop settles cleanly);
 *   - bank error commands roll rate, heavily rate-damped to kill the wobble;
 *   - a gentle pitch loop holds level flight (a tiny vertical-speed-driven
 *     pitch target keeps altitude without porpoising);
 *   - a yaw damper settles the Dutch-roll wallow the roll corrections excite.
 */
export function autopilotLevel(state, dir) {
  const att = state.att;
  const up = qRotate(att, v3(0, 1, 0));
  const right = qRotate(att, v3(0, 0, 1));
  const fwd = qRotate(att, v3(1, 0, 0));
  const c = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

  // Roll: heading error -> capped target bank -> roll-rate command.
  const db = qRotate(qConj(att), dir); // desired heading, in the body frame
  const headErr = Math.atan2(db.z, db.x); // +ve: target heading is off to the right
  const desiredBank = c(headErr * 0.9, -0.4, 0.4); // cap ~23deg
  const bank = Math.atan2(-right.y, up.y); // +ve: banked right (right wing low)
  const roll = c((desiredBank - bank) * 2.0 - state.omega.x * 0.5, -1, 1);

  // Pitch: hold the flight path level. A small vertical-speed-driven pitch
  // target trims out climb/sink; a strong rate term damps the loop. The
  // proportional gain is deliberately gentle and the rate damping high,
  // because the command travels a laggy path before it bites: controls are
  // resampled at 30 Hz, prediction/reconciliation adds a frame or two, and on
  // a low-frame-rate phone several physics ticks are integrated per rendered
  // frame off one autopilot solve. A hotter loop (the old 2.5/0.45) is stable
  // on a fast desktop but, once that delay reaches ~50 ms, breaks into a
  // sustained delay-driven porpoise — a pilot-induced oscillation — which is
  // exactly the regime the hands-off touch autopilot runs in. These gains stay
  // settled out past ~130 ms of loop delay across the whole fleet.
  const vsTarget = c(-state.vel.y * 0.03, -0.1, 0.1);
  const pitchAngle = Math.asin(c(fwd.y, -1, 1));
  const pitch = c((vsTarget - pitchAngle) * 1.6 - state.omega.z * 0.9, -0.7, 0.7);

  // Yaw: damper only.
  const yaw = c(-state.omega.y * 0.4, -0.3, 0.3);

  return { pitch, roll, yaw };
}
